/**
 * Life4Earth V2 — Chat SignalR Connection Manager
 *
 * Manages a separate SignalR hub connection for real-time chat.
 * Follows the same patterns as signalrConnection.ts (KPI hub):
 *   - Singleton connection with auto-reconnect
 *   - Transport fallback strategy
 *   - Custom events dispatched on window for React consumption
 *
 * Usage:
 *   import { connectChatHub, disconnectChatHub } from './chatConnection';
 *   connectChatHub();   // call in App.tsx on mount
 *   disconnectChatHub(); // call on unmount
 */

import * as signalR from "@microsoft/signalr";
import { getAccessToken } from '../auth/AuthContext';
import { getCurrentUserGuid } from './client';

import { API_BASE_URL } from './client';

const API_BASE = API_BASE_URL;

const CHAT_HUB_URL = `${API_BASE}/hubs/chat`;

/** Singleton connection instance. */
let connection: signalR.HubConnection | null = null;
let intentionalStop = false;
let connecting = false;

/**
 * Tracks every conversation the client has explicitly joined via
 * joinConversationGroup. Re-issued on reconnect so the user keeps
 * receiving real-time messages after SignalR drops + recovers.
 *
 * DEV-952: previously the onreconnected handler was a no-op log.
 * After every server-timeout-triggered reconnect (which is common
 * in dev when the API briefly hiccups), the user's connection was
 * alive but subscribed to no conversation groups. Symptom: open
 * ChatWindow stays stale, badge updates fire (because App.tsx's
 * NewMessage handler is global) but the message never reaches the
 * conversation pane until the user navigates away and back.
 */
const joinedConversations = new Set<string>();

/* ------------------------------------------------------------------ */
/*  Chat hub event names — must match ChatHub.Events on the backend    */
/* ------------------------------------------------------------------ */

export const ChatHubEvents = {
  NewMessage: "NewMessage",
  MessageEdited: "MessageEdited",
  MessageDeleted: "MessageDeleted",
  MessagePinned: "MessagePinned",
  ReactionUpdated: "ReactionUpdated",
  TypingIndicator: "TypingIndicator",
  ConversationInvite: "ConversationInvite",
  ParticipantJoined: "ParticipantJoined",
  ParticipantLeft: "ParticipantLeft",
  PresenceChanged: "PresenceChanged",
  PresenceBulkSync: "PresenceBulkSync",
  ReadReceipt: "ReadReceipt",
  ContactRequest: "ContactRequest",
  ContactAccepted: "ContactAccepted",
  MentionNotification: "MentionNotification",
  BatSignal: "BatSignal",
  BatSignalRetracted: "BatSignalRetracted",
  NotificationReceived: "NotificationReceived",
} as const;

/* ------------------------------------------------------------------ */
/*  Custom window events for React consumption                         */
/* ------------------------------------------------------------------ */

/** Prefix for all chat custom events dispatched on window. */
const EVENT_PREFIX = "l4e_chat_";

/**
 * Map hub events → custom window event names.
 * React components can listen via:
 *   window.addEventListener('l4e_chat_new_message', handler);
 */
const hubEventToCustomEvent: Record<string, string> = {
  [ChatHubEvents.NewMessage]: `${EVENT_PREFIX}new_message`,
  [ChatHubEvents.MessageEdited]: `${EVENT_PREFIX}message_edited`,
  [ChatHubEvents.MessageDeleted]: `${EVENT_PREFIX}message_deleted`,
  [ChatHubEvents.MessagePinned]: `${EVENT_PREFIX}message_pinned`,
  [ChatHubEvents.ReactionUpdated]: `${EVENT_PREFIX}reaction_updated`,
  [ChatHubEvents.TypingIndicator]: `${EVENT_PREFIX}typing`,
  [ChatHubEvents.ConversationInvite]: `${EVENT_PREFIX}conversation_invite`,
  [ChatHubEvents.ParticipantJoined]: `${EVENT_PREFIX}participant_joined`,
  [ChatHubEvents.ParticipantLeft]: `${EVENT_PREFIX}participant_left`,
  [ChatHubEvents.PresenceChanged]: `${EVENT_PREFIX}presence_changed`,
  [ChatHubEvents.PresenceBulkSync]: `${EVENT_PREFIX}presence_bulk_sync`,
  [ChatHubEvents.ReadReceipt]: `${EVENT_PREFIX}read_receipt`,
  [ChatHubEvents.ContactRequest]: `${EVENT_PREFIX}contact_request`,
  [ChatHubEvents.ContactAccepted]: `${EVENT_PREFIX}contact_accepted`,
  [ChatHubEvents.NotificationReceived]: `${EVENT_PREFIX}notification_received`,
};

/* ------------------------------------------------------------------ */
/*  Listeners (React components subscribe/unsubscribe)                 */
/* ------------------------------------------------------------------ */

type ChatEventHandler = (data: any) => void;
const listeners = new Map<string, Set<ChatEventHandler>>();

/**
 * Subscribe to a chat hub event. Returns an unsubscribe function.
 *
 * Usage:
 *   const unsub = onChatEvent('NewMessage', (msg) => { ... });
 *   return () => unsub(); // in useEffect cleanup
 */
export function onChatEvent(hubEvent: string, handler: ChatEventHandler): () => void {
  if (!listeners.has(hubEvent)) {
    listeners.set(hubEvent, new Set());
  }
  listeners.get(hubEvent)!.add(handler);
  return () => { listeners.get(hubEvent)?.delete(handler); };
}

/* ------------------------------------------------------------------ */
/*  Client → Server invocations                                        */
/* ------------------------------------------------------------------ */

export async function sendTypingStart(conversationId: string): Promise<void> {
  if (connection?.state === signalR.HubConnectionState.Connected) {
    await connection.invoke("StartTyping", conversationId);
  }
}

export async function sendTypingStop(conversationId: string): Promise<void> {
  if (connection?.state === signalR.HubConnectionState.Connected) {
    await connection.invoke("StopTyping", conversationId);
  }
}

export async function sendMarkRead(conversationId: string, messageId: string): Promise<void> {
  if (connection?.state === signalR.HubConnectionState.Connected) {
    await connection.invoke("MarkRead", conversationId, messageId);
  }
}

/**
 * Report a presence status change to the server.
 * Accepted values: "online" | "away". The server rejects anything else.
 * Called by the visibility/idle detector — not by user action directly.
 */
export async function sendPresenceUpdate(status: 'online' | 'away'): Promise<void> {
  if (connection?.state === signalR.HubConnectionState.Connected) {
    await connection.invoke("SetPresence", status);
  }
}

/**
 * Join the SignalR group for a conversation.
 * Must be called after creating or being invited to a conversation,
 * since OnConnectedAsync only joins groups for pre-existing conversations.
 *
 * DEV-952 fix: previously this silently no-op'd if the connection wasn't
 * `Connected` at the moment of the call — which routinely happened on
 * fresh logins, new browser sessions, or any case where the join request
 * raced the SignalR handshake. The dropped join meant the user was never
 * added to the conversation's group, so subsequent NewMessage broadcasts
 * bypassed their connection. Symptom: the open ChatWindow stayed stale
 * and only updated when the user navigated away and back, the badge
 * never reset, and the asymmetry was direction-dependent (the OTHER
 * party with an established connection saw everything fine).
 *
 * Now waits up to 5 seconds for the connection to enter `Connected` state
 * before invoking the server method, and logs a warning if it gives up.
 */
const JOIN_CONNECTION_TIMEOUT_MS = 5000;
const JOIN_POLL_INTERVAL_MS = 100;

export async function joinConversationGroup(conversationId: string): Promise<void> {
  // DEV-952: track every join so we can re-issue them on reconnect.
  joinedConversations.add(conversationId);

  const conn = connection;
  if (!conn) {
    console.warn(`[ChatHub] joinConversationGroup(${conversationId}): no connection object`);
    return;
  }

  // Fast path: already connected.
  if (conn.state === signalR.HubConnectionState.Connected) {
    try {
      await conn.invoke("JoinConversation", conversationId);
    } catch (err) {
      console.warn(`[ChatHub] joinConversationGroup(${conversationId}) invoke failed:`, err);
    }
    return;
  }

  // Slow path: wait for connection to enter Connected state, with timeout.
  const start = Date.now();
  await new Promise<void>((resolve) => {
    const handle = setInterval(() => {
      if (conn.state === signalR.HubConnectionState.Connected) {
        clearInterval(handle);
        resolve();
      } else if (Date.now() - start > JOIN_CONNECTION_TIMEOUT_MS) {
        clearInterval(handle);
        console.warn(
          `[ChatHub] joinConversationGroup(${conversationId}): timed out after ${JOIN_CONNECTION_TIMEOUT_MS}ms ` +
          `waiting for connection (state=${conn.state}). The user will not receive real-time messages ` +
          `for this conversation until they reload or navigate away and back.`
        );
        resolve();
      }
    }, JOIN_POLL_INTERVAL_MS);
  });

  // DEV-963: explicit cast — TS narrows conn.state inside this async function
  // to "not Connected" via the polling Promise's await, but at the moment we
  // reach this guard the state may legitimately be Connected (the polling
  // loop resolved on connect). Compare through the enum type.
  if ((conn.state as signalR.HubConnectionState) !== signalR.HubConnectionState.Connected) return;

  try {
    await conn.invoke("JoinConversation", conversationId);
  } catch (err) {
    console.warn(`[ChatHub] joinConversationGroup(${conversationId}) invoke failed after wait:`, err);
  }
}

/* ------------------------------------------------------------------ */
/*  Connection management                                              */
/* ------------------------------------------------------------------ */

function registerEventHandlers(conn: signalR.HubConnection): void {
  // Register all hub events → dispatch to local listeners + window CustomEvent
  for (const [hubEvent, customEvent] of Object.entries(hubEventToCustomEvent)) {
    conn.on(hubEvent, (data: any) => {
      // Notify local subscribers
      const eventListeners = listeners.get(hubEvent);
      if (eventListeners) {
        Array.from(eventListeners).forEach((handler) => {
          try { handler(data); } catch (err) {
            console.warn(`[ChatHub] Error in ${hubEvent} handler:`, err);
          }
        });
      }

      // Also dispatch window event (for cross-component coordination)
      try {
        window.dispatchEvent(new CustomEvent(customEvent, { detail: data }));
      } catch (err) {
        console.warn("[ChatHub] Failed to dispatch custom event:", err);
      }
    });
  }

  conn.onreconnecting((error) => {
    console.warn("[ChatHub] Reconnecting...", error?.message);
  });

  conn.onreconnected(async (newConnectionId) => {
    console.log(`[ChatHub] Reconnected (${newConnectionId}).`);

    // DEV-952: server-side OnConnectedAsync should auto-rejoin conversations
    // the user was a participant in at connect time, but (a) it can race the
    // reconnect and (b) any conversations created during the disconnect
    // window won't be in that set. Defensively re-issue every explicit join
    // we've tracked since this session started. JoinConversation is
    // idempotent server-side (Groups.AddToGroupAsync handles duplicates).
    if (joinedConversations.size === 0) return;
    console.log(`[ChatHub] Re-joining ${joinedConversations.size} conversation group(s) after reconnect.`);
    for (const convId of joinedConversations) {
      try {
        await conn.invoke("JoinConversation", convId);
      } catch (err) {
        console.warn(`[ChatHub] Failed to re-join ${convId} after reconnect:`, err);
      }
    }
  });

  conn.onclose((error) => {
    if (conn !== connection) return; // stale connection guard
    console.log("[ChatHub] Connection closed.", error?.message);
    connection = null;
    if (!intentionalStop) {
      setTimeout(() => connectChatHub(), 5000);
    }
  });
}

function buildConnection(options?: {
  transport?: signalR.HttpTransportType;
  skipNegotiation?: boolean;
}): signalR.HubConnection {
  // DEV-952 FIX: accessTokenFactory MUST read the current token at every call,
  // not capture it at build time. The previous code did `const token = getAccessToken()`
  // once at construction, then `accessTokenFactory: () => token || ''`. If the token
  // wasn't ready when buildConnection ran (e.g., user just logged in via OAuth
  // and AuthContext hadn't propagated the token yet), the connection would
  // permanently fall back to the fake-auth path (X-User-Guid header) which the
  // production server IGNORES (UseFakeAuth=false). Result: SignalR connection
  // established with no identity. ChatHub.OnConnectedAsync sees empty userGuid,
  // joins no groups, all NewMessage broadcasts bypass the connection forever.
  // Fix: call getAccessToken() inside accessTokenFactory so every handshake
  // (initial + reconnect) gets the latest token.
  //
  // The fake-auth fallback below is preserved for true fake-auth dev mode
  // (when getAccessToken always returns null) but no longer fires in the
  // race-window scenario.
  const initialToken = getAccessToken();
  let hubUrl = CHAT_HUB_URL;
  const headers: Record<string, string> = {};
  if (!initialToken) {
    const userGuid = getCurrentUserGuid();
    if (userGuid) {
      hubUrl = `${CHAT_HUB_URL}?userGuid=${encodeURIComponent(userGuid)}`;
      headers['X-User-Guid'] = userGuid;
    }
  }

  const conn = new signalR.HubConnectionBuilder()
    .withUrl(hubUrl, {
      accessTokenFactory: () => getAccessToken() || '',
      headers,
      ...(options?.transport !== undefined ? { transport: options.transport } : {}),
      ...(options?.skipNegotiation ? { skipNegotiation: true } : {}),
    })
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: (retryContext) => {
        const delays = [0, 2000, 5000, 10000, 30000];
        return retryContext.previousRetryCount < delays.length
          ? delays[retryContext.previousRetryCount]
          : null;
      },
    })
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  // DEV-955: align with server-side SignalR defaults (Program.cs:30-32).
  // SignalR's required relationships:
  //   client.serverTimeoutInMilliseconds  >= 2 × server.KeepAliveInterval
  //   server.ClientTimeoutInterval        >= 2 × client.keepAliveInterval
  // Server now uses 15s/30s standard defaults. Client matches:
  //   serverTimeout = 30s   (>= 2 × server's 15s ping)
  //   keepAlive     = 15s   (server's 30s timeout >= 2 × this)
  // Previous values (90s/30s) were defensive padding added during the
  // 2026-05-09 debug session when we mistakenly thought the server was
  // unstable. The actual bug was a server-side KeepAliveInterval=2min,
  // which was incompatible with the client's then-default 30s timeout.
  conn.serverTimeoutInMilliseconds = 30_000;
  conn.keepAliveIntervalInMilliseconds = 15_000;

  return conn;
}

/**
 * Start the Chat SignalR connection.
 * Same transport fallback strategy as KPI hub.
 */
export async function connectChatHub(): Promise<void> {
  if (connection || connecting) return;

  intentionalStop = false;
  connecting = true;

  const strategies: Array<{
    label: string;
    transport?: signalR.HttpTransportType;
    skipNegotiation?: boolean;
  }> = [
    { label: "default (negotiate)" },
    {
      label: "WebSocket (skip negotiate)",
      transport: signalR.HttpTransportType.WebSockets,
      skipNegotiation: true,
    },
    {
      label: "Server-Sent Events",
      transport: signalR.HttpTransportType.ServerSentEvents,
    },
    {
      label: "Long Polling",
      transport: signalR.HttpTransportType.LongPolling,
    },
  ];

  try {
    for (const strategy of strategies) {
      if (intentionalStop) break;

      let conn: signalR.HubConnection;
      try {
        conn = buildConnection({
          transport: strategy.transport,
          skipNegotiation: strategy.skipNegotiation,
        });
      } catch {
        continue;
      }

      registerEventHandlers(conn);

      try {
        await conn.start();
        connection = conn;
        console.log(`[ChatHub] Connected via ${strategy.label}`);
        return;
      } catch (err) {
        console.warn(`[ChatHub] ${strategy.label} failed:`, err);
        try { await conn.stop(); } catch { /* ignore */ }
      }
    }

    if (!intentionalStop) {
      console.warn("[ChatHub] All strategies failed. Retrying in 5s...");
      setTimeout(() => connectChatHub(), 5000);
    }
  } finally {
    connecting = false;
  }
}

/**
 * Gracefully stop the Chat SignalR connection.
 */
export async function disconnectChatHub(): Promise<void> {
  intentionalStop = true;
  connecting = false;
  const conn = connection;
  connection = null;
  if (conn) {
    try { await conn.stop(); } catch { /* ignore */ }
  }
}

/**
 * Gracefully disconnect chat hub for idle/background-tab purposes.
 * Unlike disconnectChatHub(), this allows explicit reconnect later
 * via reconnectChatAfterIdle().
 */
export async function idleDisconnectChat(): Promise<void> {
  intentionalStop = true;
  if (connection) {
    try { await connection.stop(); } catch { /* ignore */ }
    connection = null;
  }
}

/**
 * Reconnect chat hub after an idle/background disconnect.
 * Resets state and reconnects. The PresenceBulkSync event on
 * reconnect will refresh the presence store automatically.
 */
export async function reconnectChatAfterIdle(): Promise<void> {
  connection = null;
  connecting = false;
  intentionalStop = false;
  await connectChatHub();
}

/**
 * Check if the chat hub is currently connected.
 */
export function isChatConnected(): boolean {
  return connection?.state === signalR.HubConnectionState.Connected;
}

/**
 * Get the current chat connection state.
 */
export function getChatConnectionState(): string {
  if (!connection) return "Disconnected";
  switch (connection.state) {
    case signalR.HubConnectionState.Connected: return "Connected";
    case signalR.HubConnectionState.Connecting: return "Connecting";
    case signalR.HubConnectionState.Reconnecting: return "Reconnecting";
    default: return "Disconnected";
  }
}
